import { EntityRepository } from '@/interfaces'
import model from './model'
import type ObjectInterface from './interface'
import { hydrateEntityObject } from '@/entities/lib'
import graphQLClient from '@/lib/api/graphQLClient'
import { TFunction } from 'next-i18next'
import { useMemo } from 'react'
import useSWR from 'swr'
import { uppercaseFirst } from '@/lib/helpers/helpers'
import ratioDatePackageSettingsObject from '@/entities/ratioDatePackageSettings/interface'
import ratioTravelExtensionObject from '@/entities/ratioTravelExtension/interface'

import {
  ALL_DATES_QUERY,
  SINGLE_DATE_QUERY,
  DATES_DEPENDENTS_QUERY,
  CREATE_DATE_MUTATION,
  UPDATE_DATE_MUTATION,
  DELETE_DATE_MUTATION,
  FORCE_DELETE_DATE_MUTATION,
  ADD_DATE_PACKAGE_SETTINGS_MUTATION,
  REMOVE_DATE_PACKAGE_SETTINGS_MUTATION,
  CREATE_DATE_PACKAGE_SETTINGS_MUTATION,
  DELETE_DATE_PACKAGE_SETTINGS_MUTATION,
  ADD_DATE_EXTENSIONS_MUTATION,
  REMOVE_DATE_EXTENSIONS_MUTATION,
  CREATE_DATE_EXTENSIONS_MUTATION,
  DELETE_DATE_EXTENSIONS_MUTATION,
  UPDATE_DATE_EXTENSIONS_ORDER_MUTATION,
} from './graphql'

const hydrateObjects = (objects: Array<any>) => {
  return objects.map((object) => hydrateEntityObject(model, object))
}

const hydrateObject = (object: any) => {
  return hydrateEntityObject(model, object)
}
const ratioTravelDateRepository: EntityRepository = {
  getObject: async (id: string | number, includes = {}): Promise<any> => {
    const variables = { id: id, ...includes }
    return graphQLClient.request(SINGLE_DATE_QUERY, variables).then((data: any) => {
      return hydrateObject(data.date)
    })
  },

  getObjects: async (includes = {}): Promise<any> => {
    return graphQLClient.request(ALL_DATES_QUERY, includes).then((data: any) => {
      return hydrateObjects(data.dates)
    })
  },

  useObject: (id: string | number, includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includes }), [id])
    const { data, error, mutate } = useSWR(paused ? null : [SINGLE_DATE_QUERY, variables], { revalidateOnFocus: false })
    return {
      object: !data ? data : hydrateObject(data.date),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  useObjects: (includes = {}, paused = false) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => includes, [])
    const { data, error, mutate } = useSWR(paused ? null : [ALL_DATES_QUERY, variables])
    return {
      objects: !data ? data : hydrateObjects(data.dates),
      isLoading: !paused && !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  getDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    const variables = { id: id, ...includeVars }
    return graphQLClient.request(DATES_DEPENDENTS_QUERY, variables).then((data: any) => {
      return data.groupedDependents
    })
  },

  useDependents: (id: string | number, includes: []) => {
    const includeVars: { [x: string]: boolean } = {}
    includes.forEach((include) => {
      includeVars[`with${uppercaseFirst(include)}`] = true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const variables = useMemo(() => ({ id: id, ...includeVars }), [id])
    const { data, error, mutate } = useSWR([DATES_DEPENDENTS_QUERY, variables], {
      revalidateOnFocus: false,
    })
    return {
      data: !data ? data : data.groupedDependents,
      isLoading: !error && !data,
      isError: error,
      mutate: mutate,
    }
  },

  createObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(CREATE_DATE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.date)
    })
  },

  updateObject: async (data: ObjectInterface, includes = {}): Promise<any> => {
    const variables = {
      input: data,
      ...includes,
    }
    return graphQLClient.request(UPDATE_DATE_MUTATION, variables).then((data: any) => {
      return hydrateObject(data.date)
    })
  },

  deleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(DELETE_DATE_MUTATION, variables).then((data: any) => {
      return data.deleteRatioTravelDate
    })
  },

  forceDeleteObject: async (id: string | number): Promise<any> => {
    const variables = {
      input: { id: id },
    }
    return graphQLClient.request(FORCE_DELETE_DATE_MUTATION, variables).then((data: any) => {
      return data.forceDeleteRatioTravelDate
    })
  },

  assignPackageSettings: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        packageSettingsIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_DATE_PACKAGE_SETTINGS_MUTATION, variables)
  },

  unassignPackageSettings: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        packageSettingsIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_DATE_PACKAGE_SETTINGS_MUTATION, variables)
  },

  createPackageSettings: async (id: string, items: Array<ratioDatePackageSettingsObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        packageSettings: items,
      },
    }
    return graphQLClient.request(CREATE_DATE_PACKAGE_SETTINGS_MUTATION, variables)
  },

  deletePackageSettings: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        packageSettingsIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_DATE_PACKAGE_SETTINGS_MUTATION, variables)
  },

  assignExtensions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionIds: itemIds,
      },
    }
    return graphQLClient.request(ADD_DATE_EXTENSIONS_MUTATION, variables)
  },

  unassignExtensions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionIds: itemIds,
      },
    }
    return graphQLClient.request(REMOVE_DATE_EXTENSIONS_MUTATION, variables)
  },

  createExtensions: async (id: string, items: Array<ratioTravelExtensionObject>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensions: items,
      },
    }
    return graphQLClient.request(CREATE_DATE_EXTENSIONS_MUTATION, variables)
  },

  deleteExtensions: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionIds: itemIds,
      },
    }
    return graphQLClient.request(DELETE_DATE_EXTENSIONS_MUTATION, variables)
  },

  updateExtensionsOrder: async (id: string, itemIds: Array<string>): Promise<any> => {
    const variables = {
      input: {
        id: id,
        extensionIds: itemIds,
      },
    }
    return graphQLClient.request(UPDATE_DATE_EXTENSIONS_ORDER_MUTATION, variables)
  },
}

export default ratioTravelDateRepository
